import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from './icons';
import { Spinner } from './ui';

const STORAGE_KEY = 'ff.company';

/**
 * The selected company lives in one module-level store rather than in each caller's
 * own state.
 *
 * Every `useActiveCompany()` is a separate component: with `useState` the switcher
 * updated only its own copy, so the forms list kept whichever company it mounted with
 * and never reloaded. One store, one subscription, every consumer re-renders together.
 */
function readStored(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

let selectedId: string | null = readStored();
const listeners = new Set<() => void>();

function setSelectedCompanyId(companyId: string) {
  if (companyId === selectedId) return;
  selectedId = companyId;
  try { localStorage.setItem(STORAGE_KEY, companyId); } catch { /* private window */ }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  // A switch in another tab should not leave this one showing the old company's forms.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    selectedId = readStored();
    listener();
  };

  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

const getSnapshot = () => selectedId;

/**
 * Which company the studio is currently scoped to.
 *
 * This is a view preference, not a security boundary. The server decides what each
 * member may see from their own company memberships, so a tampered localStorage value
 * changes what the UI asks for, never what it is allowed to receive.
 */
export function useActiveCompany() {
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies.list(),
    staleTime: 5 * 60_000,
  });

  const companies = data?.data ?? [];
  const stored = useSyncExternalStore(subscribe, getSnapshot);

  // A stored id that no longer resolves — the company was archived, or the member lost
  // access — silently falls back rather than leaving the switcher showing nothing.
  const active = companies.find((c) => c.id === stored)
    ?? companies.find((c) => c.isDefault)
    ?? companies[0];

  return { companies, active, select: setSelectedCompanyId, isLoading };
}

export function CompanySwitcher() {
  const { companies, active, select, isLoading } = useActiveCompany();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointer = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isLoading) return <Spinner label="Loading companies" />;

  // One company is the common case and a switcher with a single entry is noise.
  if (companies.length <= 1) {
    return active ? (
      <span className="hidden items-center gap-1.5 text-xs text-[var(--ff-text-muted)] sm:flex">
        <Icon name="building" size={14} />
        {active.name}
      </span>
    ) : null;
  }

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-1.5 rounded-md border border-[var(--ff-border)]
                   bg-[var(--ff-surface)] px-2.5 py-1.5 text-sm
                   hover:bg-[var(--ff-surface-raised)]"
      >
        <Icon name="building" size={15} />
        <span className="max-w-[10rem] truncate">{active?.name ?? 'Select company'}</span>
        <Icon name="chevronDown" size={14} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Company"
          className="absolute left-0 z-30 mt-1 max-h-80 w-64 overflow-auto rounded-md border
                     border-[var(--ff-border)] bg-[var(--ff-surface)] p-1 shadow-lg"
        >
          {companies.map((company) => (
            <li key={company.id}>
              <button
                type="button"
                role="option"
                aria-selected={company.id === active?.id}
                onClick={() => {
                  select(company.id);
                  setOpen(false);
                  // Lists are company-scoped, so anything cached under the old company is
                  // now the wrong answer.
                  void queryClient.invalidateQueries({ queryKey: ['forms'] });
                  void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
                  // Land on that company's forms. Anywhere else in the studio is showing
                  // something that belongs to the company just switched away from — a
                  // form builder open on a form the new company does not contain.
                  navigate('/');
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm
                           hover:bg-[var(--ff-surface-raised)]"
              >
                <span className="flex-1 truncate">
                  {company.name}
                  {company.isDefault && (
                    <span className="ml-1.5 text-xs text-[var(--ff-text-muted)]">default</span>
                  )}
                </span>
                <span className="text-xs text-[var(--ff-text-muted)]">
                  {company.workspaceCount}
                </span>
                {company.id === active?.id && <Icon name="check" size={14} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
